// Minimal CSV export helpers for downloading the data shown in a dashboard.
// A cell is quoted only when it contains a comma, quote, or newline (internal
// quotes doubled, per RFC 4180). Rows are CRLF-joined and a UTF-8 BOM is
// prepended so Excel renders non-ASCII (e.g. Norwegian ø/æ/å in area names)
// correctly.

export type Cell = string | number | null | undefined;

// UTF-8 byte-order mark (U+FEFF). Built via char code to keep the source
// all-ASCII (no invisible character in the file).
const BOM = String.fromCharCode(0xfeff);

function csvCell(v: Cell): string {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: Cell[][]): string {
  return rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}

/**
 * The rows of a CSV as text, header row first (RFC 4180: quoted cells may hold commas, doubled quotes and line
 * breaks; lines end in CRLF or LF; a leading byte order mark is dropped). Every value stays a string, so an id like
 * 0301 keeps its zero. Throws on a quote that is never closed.
 */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const endRow = () => {
    row.push(cell);
    cell = "";
    // A blank line, such as the one after the last line end, is no row.
    if (row.length > 1 || row[0] !== "") rows.push(row);
    row = [];
  };
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch !== '"') cell += ch;
      else if (src[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else quoted = false;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") endRow();
    else if (ch !== "\r" || src[i + 1] !== "\n") cell += ch;
  }
  if (quoted) throw new Error("The CSV ends inside a quoted cell.");
  if (cell !== "" || row.length > 0) endRow();
  return rows;
}

/** A parsed CSV as one record per row, keyed by the header. An empty cell is null. Throws on a row of another width. */
export function csvRecords(table: readonly string[][]): Record<string, string | null>[] {
  const [header, ...rows] = table;
  return rows.map((row) => {
    if (row.length !== header.length) throw new Error("A CSV row does not match its header.");
    return Object.fromEntries(header.map((name, i) => [name, row[i] === "" ? null : row[i]]));
  });
}

/**
 * Several parsed CSVs as one table with one header: every column of every part, in the order they are first met, then
 * the `fill` columns no part has. A part without a column leaves it empty, unless its `fill` names a value for it.
 */
export function mergeCsvTables(parts: readonly { table: readonly string[][]; fill?: Readonly<Record<string, string>> }[]): string[][] {
  const header: string[] = [];
  const add = (name: string) => {
    if (!header.includes(name)) header.push(name);
  };
  parts.forEach((p) => p.table[0]?.forEach(add));
  parts.forEach((p) => Object.keys(p.fill ?? {}).forEach(add));
  const rows = parts.flatMap(({ table, fill }) => {
    const own = table[0] ?? [];
    const at = header.map((name) => own.indexOf(name));
    return table.slice(1).map((row) => header.map((name, i) => (at[i] >= 0 ? row[at[i]] : fill?.[name] ?? "")));
  });
  return [header, ...rows];
}

/** Build a CSV from `rows` (header row first) and trigger a browser download. */
export function downloadCsv(filename: string, rows: Cell[][]): void {
  const blob = new Blob([BOM + toCsv(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `<prefix>-<slugified label>-<YYYYMMDD-HHMM>.csv` (local time). */
export function csvFilename(prefix: string, label: string): string {
  const slug =
    label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "export";
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
  return `${prefix}-${slug}-${stamp}.csv`;
}
